/**
 * Site-wide search (D-086), as a list of kinds: jobs, quotes and purchase
 * orders, each through its own list's query (2026-10-13). A kind is added by
 * appending to `SEARCH_KINDS` — a label, how to search it, and where "see
 * all" goes — and the box draws it. Each kind is asked in parallel, each
 * group shows as its kind answers (2026-11-05: the slowest of three held the
 * other two back), and one failing kind never hides the others' hits. The
 * pure parts (the hit shape, the groups as they arrive, the keyboard model)
 * are tested.
 */

import type { JobListRow, PurchaseOrder, QuoteListRow } from '@unitedos/shared';
import { fetchJobs, fetchPurchaseOrders, fetchQuotes } from './api.js';
import { jobHref } from './jobTabs.js';
import { quoteHref } from './quoteTabs.js';

export type SearchKind = 'job' | 'quote' | 'purchase_order'; // later: 'client' | 'invoice' | 'person'

export interface SearchHit {
  readonly kind: SearchKind;
  /** Stable within its kind, for React keys and the active-descendant id. */
  readonly id: string;
  readonly title: string;
  readonly subtitle: string | null;
  readonly href: string;
}

export interface SearchKindDef {
  readonly kind: SearchKind;
  /** The group heading over its hits: a plural noun. */
  readonly label: string;
  /** The first few hits for a query; throws on a failed read. */
  readonly search: (query: string, signal: AbortSignal) => Promise<readonly SearchHit[]>;
  /** Where "See all" goes: the kind's own list, searched. */
  readonly allHref: (query: string) => string;
}

export interface SearchGroup {
  readonly kind: SearchKind;
  readonly label: string;
  readonly hits: readonly SearchHit[];
  readonly allHref: string;
  /** The kind's read failed; its hits are unknown, not none. */
  readonly failed: boolean;
}

/** How many hits a kind shows in the box; "See all" has the rest. */
export const HITS_PER_KIND = 6;

export function jobHit(row: JobListRow): SearchHit {
  const where = [row.clientName, row.propertyAddress].filter((s): s is string => !!s).join(' · ');
  return {
    kind: 'job',
    id: String(row.jobNumber),
    title: `#${row.jobNumber}${row.title ? ` · ${row.title}` : ''}`,
    subtitle: where || null,
    href: jobHref(row.jobNumber, 'general'),
  };
}

export function jobsSearchHref(query: string): string {
  return `/jobs?q=${encodeURIComponent(query)}`;
}

const JOBS: SearchKindDef = {
  kind: 'job',
  label: 'Jobs',
  search: async (query, signal) => {
    const params = new URLSearchParams({ q: query, size: String(HITS_PER_KIND) });
    const page = await fetchJobs(params, signal);
    return page.rows.map(jobHit);
  },
  allHref: jobsSearchHref,
};

/** A quote by its number, or a UnitedOS draft by its label (design 44 §1: the quotes list holds both); its client and property beneath. */
export function quoteHit(row: QuoteListRow): SearchHit {
  const where = [row.clientName, row.propertyAddress].filter((s): s is string => !!s).join(' · ');
  return {
    kind: 'quote',
    id: row.quoteNumber,
    title: `${row.draft ? row.draft.label : `Quote #${row.quoteNumber}`}${row.title ? ` · ${row.title}` : ''}`,
    subtitle: where || null,
    href: quoteHref(row.quoteNumber, 'general'),
  };
}

export function quotesSearchHref(query: string): string {
  return `/quotes?q=${encodeURIComponent(query)}`;
}

const QUOTES: SearchKindDef = {
  kind: 'quote',
  label: 'Quotes',
  search: async (query, signal) => {
    const params = new URLSearchParams({ q: query, size: String(HITS_PER_KIND) });
    const page = await fetchQuotes(params, signal);
    return page.rows.map(quoteHit);
  },
  allHref: quotesSearchHref,
};

/** The PO by its number and vendor; the number's job beneath. It opens on the Purchase orders page, every view, the row open (`?po=`). */
export function purchaseOrderHit(order: PurchaseOrder): SearchHit {
  const job = order.jobNumber === null ? null : `#${order.jobNumber}${order.jobTitle ? ` · ${order.jobTitle}` : ''}`;
  return {
    kind: 'purchase_order',
    id: String(order.id),
    title: `PO ${order.poNumber}${order.vendor ? ` · ${order.vendor}` : ''}`,
    subtitle: job,
    href: `/purchase-orders?view=all&po=${order.id}`,
  };
}

export function purchaseOrdersSearchHref(query: string): string {
  return `/purchase-orders?view=all&q=${encodeURIComponent(query)}`;
}

/** The POs' list has no page size: every match of the All view comes back and the box keeps the first few. */
const PURCHASE_ORDERS: SearchKindDef = {
  kind: 'purchase_order',
  label: 'Purchase orders',
  search: async (query, signal) => {
    const page = await fetchPurchaseOrders('all', query, null, signal);
    return page.orders.slice(0, HITS_PER_KIND).map(purchaseOrderHit);
  },
  allHref: purchaseOrdersSearchHref,
};

export const SEARCH_KINDS: readonly SearchKindDef[] = [JOBS, QUOTES, PURCHASE_ORDERS];

/** Trimmed, and empty when there is nothing to search for. */
export function normalizeQuery(raw: string): string {
  return raw.trim();
}

/** Fewer characters than this ask nothing: one letter matches nearly every row, and the next keystroke aborts it anyway. */
export const MIN_QUERY_CHARS = 2;

export function isSearchable(query: string): boolean {
  return query.length >= MIN_QUERY_CHARS;
}

/** Every kind asked at once, each group handed over as its kind answers; a kind that fails answers with `failed` rather than taking the rest down. Nothing is handed over once the signal aborts. */
export async function searchEach(query: string, signal: AbortSignal, onGroup: (group: SearchGroup) => void, kinds: readonly SearchKindDef[] = SEARCH_KINDS): Promise<void> {
  await Promise.all(
    kinds.map(async (k) => {
      let group: SearchGroup;
      try {
        const hits = await k.search(query, signal);
        group = { kind: k.kind, label: k.label, hits, allHref: k.allHref(query), failed: false };
      } catch (err) {
        if (signal.aborted) throw err;
        group = { kind: k.kind, label: k.label, hits: [], allHref: k.allHref(query), failed: true };
      }
      if (!signal.aborted) onGroup(group);
    }),
  );
}

/** What the box shows: the groups answered so far, and the text they answer. */
export interface ShownSearch {
  readonly query: string;
  readonly groups: readonly SearchGroup[];
}

/** One kind's answer onto what the box shows: the first answer to a new text replaces the last text's groups (kept until then, so the box never blanks between keystrokes), later ones join it, in the kinds' order. */
export function addGroup(shown: ShownSearch | null, query: string, group: SearchGroup, kinds: readonly SearchKindDef[] = SEARCH_KINDS): ShownSearch {
  const kept = shown?.query === query ? shown.groups.filter((g) => g.kind !== group.kind) : [];
  const order = (g: SearchGroup) => kinds.findIndex((k) => k.kind === g.kind);
  return { query, groups: [...kept, group].sort((a, b) => order(a) - order(b)) };
}

/** The arrow keys over a flat list: −1 is "nothing picked"; Down from the end wraps to nothing, Up from nothing goes to the end. */
export function moveSelection(current: number, delta: 1 | -1, count: number): number {
  if (count === 0) return -1;
  const next = current + delta;
  if (next >= count) return -1;
  if (next < -1) return count - 1;
  return next;
}

/** Whether a keypress landed somewhere the person is already typing, so "/" must not steal it. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}
